import Raven from 'raven-js';
import { useFetchPinnedViews } from './useFetchPinnedViews';
import { useSetPinnedViews } from './useSetPinnedViews';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback } from 'react';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { jsx as _jsx } from "react/jsx-runtime";
export const useRemovePinnedView = ({
  currentViewId,
  objectTypeId,
  namespace,
  onCurrentViewIdChange
}) => {
  const {
    data
  } = useFetchPinnedViews({
    objectTypeId,
    namespace
  });
  const [setPinnedViews] = useSetPinnedViews({
    objectTypeId,
    namespace
  });
  return useCallback(viewId => {
    if (!data) {
      //this more for runtime safeness, if something really bad does happen, we can gracefully degrade and fire a sentry
      const error = new Error('tyring to remove a pinned view before we know what pinned views are');
      Raven.captureException(error, {
        extra: {
          data,
          currentViewId,
          namespace,
          objectTypeId
        }
      });
      return;
    }
    const indexOfViewBeingClosed = data.pinnedViews.indexOf(viewId);
    const newViewIds = data.pinnedViews.filter(id => id !== viewId) || [];
    setPinnedViews(newViewIds).catch(e => {
      Raven.captureException(e);
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "viewsManagement.pinnedViews.mutationErrorMessage"
        }),
        type: 'danger'
      });
    });
    if (currentViewId === null || newViewIds.includes(currentViewId)) {
      /*
       * The code below this if block is trying to figure out
       * "okay, the user just closed on a view that was active, we gotta figure out what next one we should make active"
       * Kinda like if you close the current google chrome tab, you expect it to open one to the left or right of the one you just closed
       *
       * If the user doesnt have an active tab (aka when currentViewId === null) OR the active tab wasnt removed, we dont have to do anything!
       * We can just return early
       */
      return;
    }
    const determineIndexToOpenAfterClosing = () => {
      //if its a view that is open but is not pinned
      //(such as if they are over the limit, but are allowed to look at the view)
      if (indexOfViewBeingClosed === -1) {
        return newViewIds.length - 1;
      }
      if (indexOfViewBeingClosed === undefined) {
        return newViewIds.length - 1;
      }
      if (indexOfViewBeingClosed === 0) {
        return 1;
      }
      return indexOfViewBeingClosed - 1;
    };
    const indexOfNextView = determineIndexToOpenAfterClosing();
    const idOfNextView = data.pinnedViews[indexOfNextView];

    //this more for runtime safeness, if something really bad does happen, we can gracefully degrade and fire a sentry
    if (idOfNextView === undefined) {
      const error = new Error(`Could not determine idOfNextView`);
      Raven.captureException(error, {
        extra: {
          pinnedViews: data.pinnedViews,
          indexOfNextView,
          idOfNextView,
          objectTypeId,
          namespace,
          currentViewId
        }
      });
      return;
    }
    onCurrentViewIdChange(idOfNextView);
  }, [currentViewId, data, onCurrentViewIdChange, setPinnedViews, namespace, objectTypeId]);
};